import * as Yup from 'yup';

/**
 * buildWorksheetRangeUpcomingSchema
 *
 * - Does NOT use .when(...) on modules_per_week.
 * - Accepts `isActive` (boolean) to decide whether to enforce the rules.
 *
 * Usage:
 *   // compute isActive outside (e.g. in component)
 *   const isModule1Active = Number(formik.values.modules_per_week) >= 1;
 *   ...buildWorksheetRangeUpcomingSchema({ fromKey: 'module1WSFrom', toKey: 'module1WSTo', minKey: 'module1RangeMin', maxKey: 'module1RangeMax', isActive: isModule1Active, fromLabel: 'Module 1 worksheet FROM', toLabel: 'Module 1 worksheet TO' })
 */
export const buildWorksheetRangeUpcomingSchema = ({
	fromKey,
	toKey,
	minKey,
	maxKey,
	// NEW: boolean flag (compute outside)
	isActive = false,
	fromLabel = 'Worksheet FROM',
	toLabel = 'Worksheet TO',
}) => {
	const parseNumber = (v) =>
		v !== undefined && v !== null && v !== '' ? Number(v) : undefined;

	if (!isActive) {
		// Module not active — no strict validation
		return {
			[fromKey]: Yup.number().nullable(),
			[toKey]: Yup.number().nullable(),
		};
	}

	// Module is active — apply validations (required + range + from<to)
	const fromSchema = Yup.number()
		.transform((value, originalValue) => (originalValue === '' ? undefined : value))
		.typeError(`${fromLabel} must be a number`)
		.required(`Please enter ${fromLabel}`)
		.test('range-from', '', function (value) {
			const parent = this.parent || {};
			const min = parseNumber(parent[minKey]);
			const max = parseNumber(parent[maxKey]);
			const to = parseNumber(parent[toKey]);

			// required() will handle empty case, so treat undefined/null as ok here
			if (value === undefined || value === null) return true;

			const num = Number(value);
			if (Number.isNaN(num)) {
				return this.createError({ message: `${fromLabel} must be a number` });
			}

			if (min !== undefined && num < min) {
				return this.createError({ message: `${fromLabel} must be ≥ ${min}` });
			}

			if (max !== undefined && num > max) {
				return this.createError({ message: `${fromLabel} must be ≤ ${max}` });
			}

			if (to !== undefined && num >= to) {
				return this.createError({
					message: `${fromLabel} must be less than ${toLabel}`,
				});
			}

			return true;
		});

	const toSchema = Yup.number()
		.transform((value, originalValue) => (originalValue === '' ? undefined : value))
		.typeError(`${toLabel} must be a number`)
		.required(`Please enter ${toLabel}`)
		.test('range-to', '', function (value) {
			const parent = this.parent || {};
			const min = parseNumber(parent[minKey]);
			const max = parseNumber(parent[maxKey]);
			const from = parseNumber(parent[fromKey]);

			if (value === undefined || value === null) return true;

			const num = Number(value);
			if (Number.isNaN(num)) {
				return this.createError({ message: `${toLabel} must be a number` });
			}

			if (min !== undefined && num < min) {
				return this.createError({ message: `${toLabel} must be ≥ ${min}` });
			}

			if (max !== undefined && num > max) {
				return this.createError({ message: `${toLabel} must be ≤ ${max}` });
			}

			if (from !== undefined && num <= from) {
				return this.createError({
					message: `${toLabel} must be greater than ${fromLabel}`,
				});
			}

			return true;
		});

	return {
		[fromKey]: fromSchema,
		[toKey]: toSchema,
	};
};